import { Select } from "antd";
import { useField, useFormikContext } from "formik";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DEBOUNCE_TIME, DROPDOWN_SIZE } from "../../constants/constants";
import {
  debounceAsync,
  selectFilterOption,
  selectFilterOptionData,
  selectFilterSort,
  selectFilterSortData,
} from "../../utilities/helpers";
import CustomLabel from "../customLabel";

const getPopupContainer = (trigger) => trigger.parentNode;

const { Option } = Select;

const CustomSelect = ({
  name = "select",
  className = "",
  infoText,
  required,
  id,
  title,
  disabled,
  placeholder,
  options: passedOptions,
  children,
  labelClassName = "",
  showSearch = false,
  optionLabelProp = "label",
  optionValueProp = "value",
  optionDisableProp = "disabled",
  optionKeyProp = "key",
  onChange: handleChange,
  initialOptions = [],
  optionalText,
  noSortOptions = false,
  hideErrorMessage = false,
  allowClear = false,
  searchColumns = [],
  ...otherProps
}) => {
  const { validateForm } = useFormikContext();
  const InfoText = ({ infoText, children }) => {
    return (
      <>{infoText && <p className="info-text">{infoText || children}</p>}</>
    );
  };
  const [, { value, error, touched }, { setValue, setTouched }] = useField(
    name
  );
  const options = useMemo(
    () => {
      let transformedOptions = !passedOptions
        ? []
        : passedOptions.map((option) => {
            if (typeof option === "string")
              return {
                key: option,
                disabled: false,
                value: option,
                label: option,
              };
            const {
              [optionValueProp]: value,
              [optionLabelProp]: label,
              [optionKeyProp]: key = value,
              [optionDisableProp]: disabled = false,
            } = option;
            return {
              ...option,
              key,
              disabled,
              value,
              label,
            };
          });
      if (!noSortOptions || initialOptions.length > 0) {
        transformedOptions = transformedOptions.sort(selectFilterSortData);
      }
      return passedOptions
        ? [
            ...initialOptions.filter(
              (option) => (option.min || Infinity) >= passedOptions.length
            ),
            ...transformedOptions,
          ]
        : children
          ? undefined
          : initialOptions.filter((option) => (option.min || 0) >= 0);
    },
    [
      passedOptions,
      optionLabelProp,
      optionValueProp,
      optionDisableProp,
      initialOptions,
      children,
      optionKeyProp,
      noSortOptions,
    ]
  );

  const onChange = useCallback(
    (value) => {
      setValue(value);
      if (handleChange) handleChange(value);
      setTimeout(validateForm, 0);
    },
    [handleChange, validateForm, setValue]
  );

  return (
    <>
      {title && (
        <CustomLabel
          title={title}
          customClass={labelClassName}
          required={required}
          optionalText={optionalText}
          htmlFor={id}
        />
      )}
      <div>
        <Select
          id={id}
          {...otherProps}
          allowClear={allowClear}
          onChange={onChange}
          onBlur={() => {
            !touched && setTouched(true);
          }}
          // setting undefined will show the placeholder
          value={value === "" || value === null ? undefined : value}
          options={options}
          className={`${touched && error ? "error-box" : ""} ${className}`}
          name={name}
          showSearch={showSearch || (options && options.length > 5)}
          filterOption={
            options ? selectFilterOptionData(searchColumns) : selectFilterOption
          }
          filterSort={
            noSortOptions ? undefined : options ? undefined : selectFilterSort
          }
          placeholder={placeholder}
          disabled={disabled}
          getPopupContainer={getPopupContainer}
        >
          {options ? null : children}
        </Select>
        <InfoText infoText={infoText} />
      </div>
      {touched && error && !hideErrorMessage ? (
        <div className="error-label">{error}</div>
      ) : null}
    </>
  );
};

export const CustomSimpleSelect = ({
  className = "",
  required,
  id,
  title,
  disabled,
  placeholder,
  options: passedOptions,
  children,
  labelClassName = "",
  showSearch = false,
  optionLabelProp = "label",
  optionValueProp = "value",
  optionDisableProp = "disabled",
  onChange: handleChange,
  initialOptions = [],
  optionalText,
  noSortOptions = false,
  allowClear = false,
  loadMoreData,
  searchColumns = [],
  customFilterOptionData,
  customFilterSortData,
  ...otherProps
}) => {
  const [state, setState] = useState({
    searchQuery: "",
    pageNumber: 2,
    loading: true,
    count: undefined,
    searching: false,
    error: false,
  });

  useEffect(
    () => {
      if (loadMoreData && !state.searchQuery) {
        loadMoreData(1)
          .then((count) => {
            setState((st) => ({
              ...st,
              count,
              loading: false,
              searching: false,
            }));
          })
          .catch(() => {
            setState((st) => ({
              ...st,
              loading: false,
              searching: false,
              error: true,
            }));
          });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [state.searchQuery]
  );

  const options = useMemo(
    () => {
      let transformedOptions = (passedOptions || []).map(
        ({
          [optionValueProp]: value,
          [optionLabelProp]: label,
          key = value,
          [optionDisableProp]: disabled = false,
          ...rest
        }) => {
          return {
            ...rest,
            key,
            disabled,
            value,
            label,
          };
        }
      );
      if (!noSortOptions && passedOptions)
        transformedOptions = transformedOptions.sort(
          customFilterSortData || selectFilterSortData
        );

      transformedOptions = passedOptions
        ? [
            ...initialOptions.filter(
              (option) => (option.min || Infinity) >= passedOptions.length
            ),
            ...transformedOptions,
          ]
        : children
          ? undefined
          : initialOptions.filter((option) => (option.min || 0) >= 0);

      if (state.searchQuery) {
        const filterOptionData = customFilterOptionData
          ? customFilterOptionData
          : selectFilterOptionData(searchColumns);

        transformedOptions = transformedOptions?.filter(
          (option) =>
            !["all", "main"].includes(option.value) &&
            filterOptionData(state.searchQuery, option)
        );
      }

      return transformedOptions;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      passedOptions,
      optionLabelProp,
      optionValueProp,
      optionDisableProp,
      initialOptions,
      children,
      noSortOptions,
      state.searchQuery,
      searchColumns,
    ]
  );
  const onChange = useCallback(
    (value) => {
      setState((st) => ({ ...st, searchQuery: "" }));
      if (handleChange) handleChange(value);
    },
    [handleChange]
  );

  const reachedBottom = useRef(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadMoreDataDebounced = useCallback(
    debounceAsync(loadMoreData, DEBOUNCE_TIME, false),
    [loadMoreData]
  );

  const handleScroll = (e) => {
    const listContainer = e.target;

    const bottom =
      Math.floor(listContainer.scrollHeight - listContainer.scrollTop) ===
      listContainer.clientHeight;

    const hasMoreData = ![undefined, null, ""].includes(state.count)
      ? state.count > DROPDOWN_SIZE &&
        state.pageNumber <= Math.ceil(state.count / DROPDOWN_SIZE)
      : true;

    if (bottom && !reachedBottom.current && hasMoreData) {
      reachedBottom.current = true;
      setState((st) => ({ ...st, loading: true }));
      loadMoreData(state.pageNumber, state.searchQuery, true)
        .then((count) => {
          reachedBottom.current = false;
          setState((st) => ({
            ...st,
            count,
            loading: false,
            pageNumber: st.pageNumber + 1,
            error: false,
          }));
        })
        .catch(() => {
          reachedBottom.current = false;
          setState((st) => ({
            ...st,
            loading: false,
            error: true,
          }));
        });
    }
  };

  const handleSearch = (searchQuery) => {
    setState((st) => ({
      ...st,
      pageNumber: 2,
      searchQuery,
      loading: true,
      searching: true,
    }));
    loadMoreDataDebounced(1, searchQuery, false)
      .then(() => {
        setState((st) => ({ ...st, loading: false, searching: false }));
      })
      .catch(() => {
        setState((st) => ({
          ...st,
          loading: false,
          searching: false,
          error: true,
        }));
      });
    reachedBottom.current = false;
  };

  return (
    <>
      {title && (
        <CustomLabel
          title={title}
          customClass={labelClassName}
          required={required}
          htmlFor={id}
        />
      )}
      <Select
        id={id}
        {...otherProps}
        allowClear={allowClear}
        onChange={onChange}
        className={className}
        showSearch={showSearch}
        filterOption={
          loadMoreData
            ? () => true
            : options
              ? customFilterOptionData
                ? customFilterOptionData
                : selectFilterOptionData(searchColumns)
              : customFilterOptionData
                ? customFilterOptionData
                : selectFilterOption
        }
        filterSort={
          loadMoreData
            ? () => true
            : noSortOptions
              ? undefined
              : options
                ? customFilterSortData || selectFilterSortData
                : customFilterSortData || selectFilterSort
        }
        placeholder={placeholder}
        disabled={disabled}
        getPopupContainer={getPopupContainer}
        onPopupScroll={loadMoreData ? handleScroll : null}
        onWheel={loadMoreData ? handleScroll : null}
        onSearch={loadMoreData || options ? handleSearch : null}
        listHeight={
          window.innerHeight < 600 ? 130 : window.innerHeight < 700 ? 150 : 200
        }
        options={loadMoreData ? undefined : options}
      >
        {options && (
          <>
            {!state.searching &&
              options.map((option) => (
                <Option
                  className={option.value}
                  value={option.value}
                  key={option.value}
                >
                  {option.label}
                </Option>
              ))}
            {loadMoreData &&
              state.loading && (
                <Option
                  value="loading"
                  key="loading"
                  className="unclickable-option"
                  disabled
                >
                  Loading...
                </Option>
              )}
            {loadMoreData &&
              !state.loading &&
              state.error && (
                <Option
                  disabled
                  value="error"
                  key="error"
                  className="unclickable-option"
                >
                  Error loading data!
                </Option>
              )}
          </>
        )}

        {!options && children}
      </Select>
    </>
  );
};

export default CustomSelect;
